import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, useLabel } from "./adminLang";
import { IMG_LABELS, mediaUrl } from "../content/images";
import { Upload, Trash2 } from "lucide-react";

const MEDIA_SLOTS = [{ key: "logo", label: "Logo du site (remplace le placeholder)" }, ...Object.entries(IMG_LABELS).map(([key, label]) => ({ key, label }))];

const SECTIONS = [
  {
    key: "claims", label: "Allégations techniques (modifiables / dépubliables)",
    fields: [
      { key: "ip_transit_capacity_fr", label: "Capacité IP Transit FR" }, { key: "ip_transit_capacity_en", label: "Capacité IP Transit EN" },
      { key: "ip_transit_capacity_note_fr", label: "Note capacité FR" }, { key: "ip_transit_capacity_note_en", label: "Note capacité EN" },
      { key: "sla_text_fr", label: "Texte SLA FR", textarea: true }, { key: "sla_text_en", label: "Texte SLA EN", textarea: true },
      { key: "fixed_wireless_capacity_fr", label: "Débit sans fil FR", textarea: true }, { key: "fixed_wireless_capacity_en", label: "Débit sans fil EN", textarea: true },
      { key: "show_tv_bouquet", label: "Afficher le bouquet TV (hors navigation principale)", checkbox: true },
      { key: "tv_bouquet_label_fr", label: "Libellé TV FR" }, { key: "tv_bouquet_label_en", label: "Libellé TV EN" },
    ],
  },
  {
    key: "contact", label: "Coordonnées de l'entreprise",
    fields: [
      { key: "hq_name", label: "Nom (siège)" },
      { key: "hq_address_fr", label: "Adresse siège FR", textarea: true }, { key: "hq_address_en", label: "Adresse siège EN", textarea: true },
      { key: "phone_drc", label: "Téléphone RDC" }, { key: "phone_sa", label: "Téléphone Afrique du Sud" },
      { key: "email", label: "E-mail" }, { key: "website", label: "Site web" },
      { key: "rccm", label: "RCCM" }, { key: "id_nat", label: "ID. NAT." },
    ],
  },
  {
    key: "office_hours", label: "Horaires d'ouverture",
    fields: [{ key: "fr", label: "Horaires FR" }, { key: "en", label: "Horaires EN" }],
  },
  {
    key: "social_links", label: "Réseaux sociaux (vide = non affiché)",
    fields: [
      { key: "linkedin", label: "LinkedIn" }, { key: "twitter", label: "X / Twitter" },
      { key: "facebook", label: "Facebook" }, { key: "instagram", label: "Instagram" }, { key: "youtube", label: "YouTube" },
    ],
  },
  {
    key: "notify_email", label: "Notifications internes",
    fields: [{ key: "email", label: "E-mail de notification des leads" }],
  },
  {
    key: "support", label: "Support réseau (affiché uniquement si activé et vérifié)",
    fields: [
      { key: "enabled", label: "Activer l'option support", checkbox: true },
      { key: "phone", label: "Numéro de support" }, { key: "portal_url", label: "URL du portail support" },
    ],
  },
  {
    key: "data_retention", label: "Conservation des données (mois)",
    fields: [{ key: "leads_months", label: "Leads (mois)", number: true }, { key: "messages_months", label: "Messages (mois)", number: true }],
  },
];

const SMTP_SECTION = {
  key: "smtp", label: "SMTP — client e-mail interne (e-mails sortants)",
  fields: [
    { key: "host", label: "Hôte SMTP" }, { key: "port", label: "Port", number: true },
    { key: "username", label: "Utilisateur" }, { key: "password", label: "Mot de passe", password: true },
    { key: "from_address", label: "Adresse d'expédition" },
    { key: "start_tls", label: "STARTTLS", checkbox: true },
    { key: "use_ssl", label: "SSL (port 465)", checkbox: true },
  ],
};

const SettingsPage = () => {
  const { canWrite, isSuper, user } = useAdmin();
  const { tr } = useAdminLang();
  const L = useLabel();
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState("");
  const [uploading, setUploading] = useState("");
  const [mediaError, setMediaError] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings) return <p className="text-white/40 text-sm">{tr("Chargement…", "Loading…")}</p>;

  const saveSection = async (key) => {
    await api.put(`/admin/settings/${key}`, settings[key] || {});
    setSaved(key); setTimeout(() => setSaved(""), 2000);
  };

  const media = settings.media || {};
  const uploadMedia = async (slotKey, file) => {
    if (!file) return;
    setUploading(slotKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload?sub=media", fd);
      const next = { ...media, [slotKey]: res.data.path };
      setSettings({ ...settings, media: next });
      await api.put("/admin/settings/media", next);
      setSaved("media"); setTimeout(() => setSaved(""), 2000);
    } catch (e) {
      setSaved("");
      setMediaError(e?.response?.data?.detail || "Échec du téléversement. Réessayez.");
      setTimeout(() => setMediaError(""), 4000);
    } finally { setUploading(""); }
  };
  const clearMedia = async (slotKey) => {
    const next = { ...media }; delete next[slotKey];
    setSettings({ ...settings, media: next });
    await api.put("/admin/settings/media", next);
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      // Persist current SMTP fields first so the just-entered creds are used.
      await api.put("/admin/settings/smtp", settings.smtp || {});
      const to = (testTo || user?.email || "").trim();
      const res = await api.post("/admin/smtp/test", { to });
      setTestResult({ ...res.data, to });
    } catch (e) {
      setTestResult({ status: "failed", error: e?.response?.data?.detail || "Erreur réseau" });
    } finally { setTesting(false); }
  };

  const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF6600]";

  const renderSection = (section) => {
    if (section.key === "smtp" && !isSuper) return null;
    const values = settings[section.key] || {};
    const setVal = (k, v) => setSettings({ ...settings, [section.key]: { ...values, [k]: v } });
    return (
      <section key={section.key} className="bg-[#101214] border border-white/10 rounded-2xl p-6" data-testid={`settings-${section.key}`}>
        <h2 className="font-bold text-white text-sm">{L(section.label)}</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {section.fields.map((f) => (
            <div key={f.key} className={f.textarea ? "sm:col-span-2" : ""}>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-white/45 mb-1.5">{L(f.label)}</label>
              {f.textarea && <textarea rows={2} value={values[f.key] ?? ""} onChange={(e) => setVal(f.key, e.target.value)} className={inputCls} disabled={!canWrite} data-testid={`settings-field-${section.key}-${f.key}`} />}
              {f.checkbox && <input type="checkbox" checked={!!values[f.key]} onChange={(e) => setVal(f.key, e.target.checked)} className="w-4 h-4 accent-[#FF6600]" disabled={!canWrite} data-testid={`settings-field-${section.key}-${f.key}`} />}
              {!f.textarea && !f.checkbox && (
                <input type={f.number ? "number" : f.password ? "password" : "text"} value={values[f.key] ?? ""}
                  onChange={(e) => setVal(f.key, f.number ? Number(e.target.value) : e.target.value)} className={inputCls} disabled={!canWrite} data-testid={`settings-field-${section.key}-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        {canWrite && (
          <button onClick={() => saveSection(section.key)} data-testid={`settings-save-${section.key}`}
            className="mt-5 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-6 py-2.5 transition-colors">
            {saved === section.key ? tr("Enregistré ✓", "Saved ✓") : tr("Enregistrer", "Save")}
          </button>
        )}
        {section.key === "smtp" && isSuper && (
          <div className="mt-6 pt-5 border-t border-white/10" data-testid="smtp-test-block">
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/45 mb-2">{tr("Tester l'envoi", "Test delivery")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                placeholder={user?.email || "email@exemple.com"} className={`${inputCls} max-w-xs`} data-testid="smtp-test-recipient" />
              <button onClick={sendTest} disabled={testing} data-testid="smtp-test-send"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#FF6600] hover:text-[#08090A] text-white text-xs font-black rounded-full px-6 py-2.5 disabled:opacity-60 transition-colors">
                {testing ? tr("Envoi…", "Sending…") : tr("Envoyer un e-mail test", "Send test email")}
              </button>
            </div>
            {testResult && (
              <p className={`mt-3 text-xs font-bold ${testResult.status === "sent" ? "text-green-400" : testResult.status === "logged" ? "text-[#FFC000]" : "text-red-400"}`} data-testid="smtp-test-result">
                {testResult.status === "sent" && tr(`E-mail test envoyé à ${testResult.to} ✓`, `Test email sent to ${testResult.to} ✓`)}
                {testResult.status === "logged" && tr("SMTP non configuré — renseignez l'hôte puis enregistrez.", "SMTP not configured — enter the host then save.")}
                {testResult.status === "failed" && tr(`Échec : ${testResult.error}`, `Failed: ${testResult.error}`)}
              </p>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="text-2xl font-black text-white">{tr("Paramètres", "Settings")}</h1>
      <div className="mt-6 space-y-6 max-w-4xl">
        {[...SECTIONS, SMTP_SECTION].map(renderSection)}

        <section className="bg-[#101214] border border-white/10 rounded-2xl p-6" data-testid="settings-media">
          <h2 className="font-bold text-white text-sm">{tr("Médias du site (logo & images — remplaçables)", "Site media (logo & images — replaceable)")}</h2>
          <p className="mt-1 text-[11px] text-white/40">{tr("Téléversez une image pour remplacer le visuel par défaut. Laisser vide = image curatée par défaut.", "Upload an image to replace the default visual. Leave empty = curated default image.")}</p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {MEDIA_SLOTS.map((slot) => (
              <div key={slot.key} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl p-3" data-testid={`media-slot-${slot.key}`}>
                <div className="w-14 h-14 rounded-lg bg-white/5 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                  {media[slot.key]
                    ? <img src={mediaUrl(media[slot.key])} alt="" className="w-full h-full object-contain" />
                    : <span className="text-[10px] text-white/30">{tr("défaut", "default")}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-white/80 truncate">{L(slot.label)}</p>
                  {canWrite && (
                    <div className="mt-1.5 flex items-center gap-3">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-[#FF9900] hover:text-[#FF6600]">
                        <Upload size={12} /> {uploading === slot.key ? tr("Envoi…", "Uploading…") : tr("Remplacer", "Replace")}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia(slot.key, e.target.files?.[0])} data-testid={`media-upload-${slot.key}`} />
                      </label>
                      {media[slot.key] && (
                        <button onClick={() => clearMedia(slot.key)} className="inline-flex items-center gap-1 text-[11px] font-bold text-white/40 hover:text-red-400" data-testid={`media-clear-${slot.key}`}>
                          <Trash2 size={12} /> {tr("Réinitialiser", "Reset")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {saved === "media" && <p className="mt-4 text-xs font-bold text-[#FF9900]" data-testid="media-saved">{tr("Enregistré ✓", "Saved ✓")}</p>}
          {mediaError && <p className="mt-4 text-xs font-bold text-red-400" data-testid="media-error">{mediaError}</p>}
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
